import type { Layout } from '../../data/types';
import type { Vec2 } from '../../geo/types';
import type { Rng } from '../../core/rng';
import { centroid } from '../../geo/polygon';
import { distance, type StreetGraph } from './streetGraph';
import type { LifeClearance } from './routes';
import { pedestrianPath } from './pedestrianPath';
import { corners, doorTravel, heading, move, stay, travel, type Access } from './itinerary';
import { planWatchRound } from './watchRound';
import { burglarRoutines } from './nightBurglars';
import type { CitizenRole, LifeRoute, LifeStep } from './types';

/*
 * The city after the evening bell (docs/NIGHT.md). The civic guard keeps the night watch: it
 * musters at the town hall at nine and holds the three land gates the sources name. The schout
 * enforces the ordinances with his servants. Anyone else abroad must carry a lantern, so the
 * residents with an errand do, and the burglars (nightBurglars.ts) do not. How many of each walk
 * here, and their rounds, are estimates.
 */
export const WATCH = {
  muster: 'landmark-oude-stadhuis',
  musterSquare: 'square-dam',
  /** Minutes after midnight at which the watch musters, and the hour by which it has stood down. */
  musterMinutes: 21 * 60,
  standDownMinutes: 9 * 60,
  gates: ['gate-haarlemmerpoort', 'gate-sint-antoniespoort', 'gate-regulierspoort'],
  /* The harbour's mouth, where the boom closes the Damrak at night: a post at the bridge's head.
     No source read here places one; it is an estimate. */
  harbourHead: 'landmark-nieuwe-brug-east-headhouse',
  /** Metres a round runs out along the streets before it turns back. */
  roundM: 320,
  /** Least metres between where the watch's round from the Dam ends and where the schout's does. */
  roundsApartM: 120,
  /* Beside the rounds from the posts, pairs that walk the quarters between them: as many as find
     a street this far from every round already walked. The posts were given out at the muster;
     how many pairs went where is not recorded, so the number is an estimate, raised at the user's
     request for more patrols (7 October 2026). */
  quarterRounds: 10,
  quartersApartM: 90,
  /** A quarter's round is walked only where the streets carry it this far out. */
  quarterLeastM: 200,
  residents: 30,
} as const;

const RESIDENTS: readonly CitizenRole[] = [
  'trader',
  'craftsman',
  'sailor',
  'trader',
  'shipmaster',
  'craftsman',
];
const perpendicular = (angle: number): Vec2 => [Math.sin(angle), -Math.cos(angle)];
/* How many of the streets farthest from the watch are tried for one quarter's round. */
/* A walker's body, in metres from its middle: the radius every route of the cast is given. */
const BODY_RADIUS = 0.48;
const QUARTER_TRIES = 120;
const STUB_M = 45;
const TOWN_CELL_M = 40;

function route(
  id: string,
  role: CitizenRole,
  steps: readonly LifeStep[],
  speed: number,
  phase: number
): LifeRoute {
  return {
    id,
    role,
    streetId: 'night',
    a: steps[0]!.from,
    b: steps.at(-1)!.to,
    radius: BODY_RADIUS,
    speed,
    scale: 1,
    phase,
    routine: steps,
    night: true,
    ...(role === 'watchman' || role === 'schout' ? { watch: true } : {}),
  };
}

function stand(at: Vec2, facing: number, seconds: number): LifeStep {
  return { from: at, to: at, seconds, action: 'wait', heading: facing, prop: 'lantern' };
}

const duration = (steps: readonly LifeStep[]): number =>
  steps.reduce((sum, step) => sum + step.seconds, 0);

export function nightRoutines(
  layout: Layout,
  graph: StreetGraph,
  homes: readonly Access[],
  taken: ReadonlySet<string>,
  clearance: LifeClearance,
  rng: Rng
): LifeRoute[] {
  const routes: LifeRoute[] = [];
  const booked: Vec2[] = [];
  const stations: Vec2[] = [];
  const hall = layout.landmarks.find((landmark) => landmark.id === WATCH.muster);
  const square = layout.squares.find((s) => s.id === WATCH.musterSquare);
  if (!hall || !square) return routes;
  const hallCentre = centroid(hall.footprint),
    damCentre = centroid(square.polygon);
  /* The watch gathers before the hall, on the Dam: the node of the square nearest the hall. */
  const muster = graph.nodes.reduce<number | null>((best, node, i) => {
    if (node.streetId !== WATCH.musterSquare) return best;
    return best === null ||
      distance(node.position, hallCentre) < distance(graph.nodes[best]!.position, hallCentre)
      ? i
      : best;
  }, null);
  if (muster === null) return routes;
  const musterAt = graph.nodes[muster]!.position;

  const post = (name: string, at: Vec2, facing: number): void => {
    const side = perpendicular(facing);
    for (const reach of [0.9, -0.9]) {
      const spot = [0.9, 0.45].map((d) => move(at, side, Math.sign(reach) * d));
      const clear = spot.find((p) => clearance.clear(p, 0.5));
      if (!clear) continue;
      booked.push(clear);
      stations.push(clear);
      routes.push(
        route(`watchman-${name}-${routes.length}`, 'watchman', [stand(clear, facing, 3600)], 0.9, 0)
      );
    }
  };
  post('hall', musterAt, heading(hallCentre, damCentre));

  /* The street graph is in pieces that no route crosses between (bridges and narrow alleys are
     left out of it), so each post walks its round over the streets that can be reached from where
     it stands: to the node farthest along them within the round's length, and back. */
  const reach = (
    from: number,
    limit: number,
    allowed: (node: number) => boolean = () => true
  ): { node: number; along: number } => {
    let best = -1,
      cost = 0;
    for (const [node, along] of graph.within(from, limit))
      if (along > cost && allowed(node)) [best, cost] = [node, along];
    return { node: best, along: cost };
  };
  const farthest = (from: number, limit: number, apart?: Vec2): number | null => {
    const { node } = reach(
      from,
      limit,
      (n) => !apart || distance(graph.nodes[n]!.position, apart) >= WATCH.roundsApartM
    );
    return node < 0 ? null : node;
  };
  /* Every street the watch walks, for those who would keep out of its way. */
  const patrolled: Vec2[] = [];
  const patrol = (from: number, to: number | null, order: number): Vec2 | null => {
    const path = to === null ? null : graph.path(from, to);
    if (!path || path.length < 3) return null;
    const reservations = [...booked];
    const pair = [0.45, 1.5].map((lane) =>
      planWatchRound(path, clearance, lane, 0.92, reservations, stations)
    );
    if (pair.some((steps) => steps === null)) return null;
    booked.push(...reservations.slice(booked.length));
    patrolled.push(...path);
    for (const [member, steps] of pair.entries()) {
      if (!steps) continue;
      routes.push(
        route(
          `watchman-round-${routes.length}`,
          'watchman',
          steps,
          0.92,
          /* Each round starts part-way along, so a still of the first instant shows the watch
             out on its streets and not gathered at its posts. */
          duration(steps) * ((0.18 + 0.19 * order) % 1) - member * 0.9
        )
      );
    }
    return path.at(-1)!;
  };
  const hallRound = patrol(muster, farthest(muster, WATCH.roundM), 0);

  WATCH.gates.forEach((id, n) => {
    const gate = layout.fortifications.gates.find((g) => g.id === id);
    if (!gate) return;
    /* Inside the gate, on the street that leads from it into the city. */
    const inward = move(
      gate.pos,
      [
        (musterAt[0] - gate.pos[0]) / distance(gate.pos, musterAt),
        (musterAt[1] - gate.pos[1]) / distance(gate.pos, musterAt),
      ],
      gate.depth / 2 + 6
    );
    const node = graph.nearest(inward, 40);
    if (node === null) return;
    const at = graph.nodes[node]!.position;
    post(id.replace('gate-', ''), at, heading(at, gate.pos));
    patrol(node, farthest(node, WATCH.roundM), n + 1);
  });

  const head = layout.landmarks.find((landmark) => landmark.id === WATCH.harbourHead);
  const headNode = head ? graph.nearest(centroid(head.footprint), 45) : null;
  if (head && headNode !== null) {
    const at = graph.nodes[headNode]!.position;
    post('harbour', at, heading(at, centroid(head.footprint)));
  }

  /* The schout's round leaves the Dam another way than the watch's own. */
  const schoutEnd = farthest(muster, WATCH.roundM, hallRound ?? undefined);
  const schoutPath = schoutEnd === null ? null : graph.path(muster, schoutEnd);
  if (schoutPath && schoutPath.length >= 3) {
    patrolled.push(...schoutPath);
    const party: readonly [CitizenRole, number, number][] = [
      ['watchman', 0.35, 1.4],
      ['schout', 1.0, 0],
      ['watchman', 1.7, -1.3],
    ];
    for (const [role, lane, lead] of party) {
      const steps = planWatchRound(schoutPath, clearance, lane, 0.86, booked, stations);
      if (!steps) continue;
      /* The schout walks with his arms in his gown; his servants carry the party's lights. */
      const carried =
        role === 'schout' ? steps.map(({ prop: _prop, ...step }): LifeStep => step) : steps;
      routes.push(
        route(`${role}-schout-${routes.length}`, role, carried, 0.86, duration(steps) * 0.3 + lead)
      );
    }
  }

  const inTown = townNodes(graph, homes);
  quarterRounds(graph, inTown, patrolled, WATCH.gates.length + 1, (from, order) => {
    const end = reach(from, WATCH.roundM, (node) => inTown.has(node));
    return end.along >= WATCH.quarterLeastM && patrol(from, end.node, order) !== null;
  });

  const free = homes.filter((home) => !taken.has(home.entry.id));
  const stride = Math.max(1, Math.floor(free.length / WATCH.residents));
  const visited = new Set<string>();
  for (let n = 0; n < WATCH.residents; n++) {
    const home = free[n * stride];
    if (!home) break;
    const host = free
      .filter((other) => {
        const d = distance(home.approach, other.approach);
        return other !== home && !visited.has(other.entry.id) && d > 40 && d < 160;
      })
      .map((other) => ({ other, path: graph.path(home.node, other.node) }))
      .find(({ path }) => path);
    if (!host?.path) continue;
    const speed = rng.range(0.9, 1.1),
      lane = rng.range(0.5, 1.4);
    const there = corners(
        pedestrianPath([home.approach, ...host.path, host.other.approach], clearance, lane)
      ),
      back = corners(
        pedestrianPath(
          [host.other.approach, ...[...host.path].reverse(), home.approach],
          clearance,
          lane
        )
      );
    const steps: LifeStep[] = [];
    stay(steps, home, rng.range(40, 200), 'inside');
    doorTravel(steps, home, true, speed, 'lantern');
    travel(steps, there, speed, 'lantern');
    doorTravel(steps, host.other, false, speed, 'lantern');
    stay(steps, host.other, rng.range(60, 140), 'inside');
    doorTravel(steps, host.other, true, speed, 'lantern');
    travel(steps, back, speed, 'lantern');
    doorTravel(steps, home, false, speed, 'lantern');
    const role = RESIDENTS[n % RESIDENTS.length]!;
    visited.add(home.entry.id).add(host.other.entry.id);
    routes.push({
      ...route(`${role}-night-${routes.length}`, role, steps, speed, rng.range(0, duration(steps))),
      streetId: home.entry.streetId,
      a: home.approach,
      b: host.other.approach,
      scale: rng.range(0.94, 1.025),
      ...(role === 'trader' && n % 2 === 1 ? { appearance: 'woman' as const } : {}),
    });
  }
  routes.push(
    ...burglarRoutines(
      graph,
      free.filter((home) => !visited.has(home.entry.id)),
      clearance,
      patrolled,
      rng.fork('burglars')
    )
  );
  return routes;
}

/*
 * The street-graph nodes among the houses. The graph runs on along the country roads for
 * kilometres, and the watch walks the town: a node counts when a house door of the cast's stands
 * in its cell of the grid or in a cell beside it.
 */
function townNodes(graph: StreetGraph, homes: readonly Access[]): ReadonlySet<number> {
  const cell = (p: Vec2): readonly [number, number] => [
    Math.floor(p[0] / TOWN_CELL_M),
    Math.floor(p[1] / TOWN_CELL_M),
  ];
  const lived = new Set(homes.map((home) => cell(home.approach).join()));
  const town = new Set<number>();
  graph.nodes.forEach((node, i) => {
    const [x, y] = cell(node.position);
    for (let dx = -1; dx <= 1; dx++)
      for (let dy = -1; dy <= 1; dy++) if (lived.has(`${x + dx},${y + dy}`)) town.add(i);
  });
  return town;
}

/*
 * The rounds through the quarters: each starts at the street farthest from every street the watch
 * walks already, so the pairs spread over the city instead of following one another. `walk` plans
 * the round from a node and says whether one could be walked from there; what it walks is added to
 * `patrolled` by the caller's own patrol.
 */
function quarterRounds(
  graph: StreetGraph,
  inTown: ReadonlySet<number>,
  patrolled: Vec2[],
  firstOrder: number,
  walk: (from: number, order: number) => boolean
): void {
  const town = [...inTown],
    gaps = new Map(town.map((i) => [i, Infinity]));
  let reckoned = 0;
  for (let n = 0; n < WATCH.quarterRounds; n++) {
    for (const p of patrolled.slice(reckoned))
      for (const i of town)
        gaps.set(i, Math.min(gaps.get(i)!, distance(graph.nodes[i]!.position, p)));
    reckoned = patrolled.length;
    const open = town
      .filter((i) => gaps.get(i)! >= WATCH.quartersApartM)
      .sort((a, b) => gaps.get(b)! - gaps.get(a)! || a - b);
    /* A street far from the watch may be a stub no round fits in: the next farthest that is not
       beside a stub already tried is tried instead. */
    const stubs: Vec2[] = [];
    const walked = open.some((from) => {
      const at = graph.nodes[from]!.position;
      if (stubs.length >= QUARTER_TRIES || stubs.some((stub) => distance(stub, at) < STUB_M))
        return false;
      if (walk(from, firstOrder + n)) return true;
      stubs.push(at);
      return false;
    });
    if (!walked) return;
  }
}
